import { Progress, Toaster, toast } from "@pdfking/components"
import { ToNumber, POS_INFINITY } from "@pdfking/math"
import { Items } from "@pdfking/tfunc"
import { useEffect } from "preact/hooks"

import type { CreateTracking } from "#public"

import { useTranslation, useTranslationTyped } from "../i18n/useTranslation"
import { UniqueId } from "../utils/Counters"

const TOAST_ID = "pdfview-tracking"

type Translations = Record<string, ((ctx?: unknown) => string) | undefined>

// One column: a label above a Progress bar above a counts line. A newly mounted
// column animates in from collapsed+transparent to its flex share — Tailwind
// v4's `starting:` variant sets the @starting-style (flex-grow 0, opacity 0)
// and `transition-all` eases to `flex-1`. Chromium transitions flex-grow, so
// the siblings slide over to make room in the same motion — pure CSS, no mount
// bookkeeping.
function Column({
  label,
  value,
  detail,
  order = 0n,
  error = false,
}: {
  label: string
  value: number
  detail: string
  order?: bigint
  error?: boolean
}) {
  return (
    <div
      className="flex min-w-0 flex-1 flex-col gap-1 overflow-hidden opacity-100 transition-all duration-300 ease-out starting:grow-0 starting:opacity-0"
      style={{ order: ToNumber(order) }}
    >
      <span className={`truncate text-xs font-medium ${error ? "text-destructive" : ""}`}>
        {label}
      </span>
      <Progress
        className="[&_[data-slot=progress-indicator]]:duration-700 [&_[data-slot=progress-indicator]]:ease-out"
        value={value}
      />
      <span className="truncate text-xs text-muted-foreground">{detail}</span>
    </div>
  )
}

// A row of equal-width columns, one per task name — the same per-task breakdown
// apps/frontend's Messages.tsx renders as its bottom progress strip, split
// across the two @pdfking/pdfengine i18n libraries it recomposes as
// "{{trackers}}: {{trackers_desc}}": "trackers" is the static label (e.g.
// "Scarico") above the bar, "trackers_desc" the counts (e.g. "2/3 file: 4/5
// mb") below it. Reads `tracking.trackers` (the computed signal) in its own
// render, exactly like Messages.tsx reads it in render() — so as tasks advance
// preact just diffs this subtree in place. The toast around it is mounted once
// and never re-created, so sonner's enter/exit animation never re-runs. Once
// the store empties (right before the toast dismisses) there are no trackers to
// show, so it falls back to a single full "done" column — keeping the toast
// visually identical as it leaves instead of flashing empty.
function TrackingBars({
  tracking,
  labels,
  descriptions,
  done,
}: {
  tracking: ReturnType<typeof CreateTracking>
  labels: Translations
  descriptions: Translations
  done: string
}) {
  const rows = Items(tracking.trackers)
  return (
    <div className="flex w-full gap-3">
      {rows.length ? (
        rows.map(([name, { title, aggregated, status }]) => (
          <Column
            detail={descriptions[title]?.(aggregated) ?? ""}
            error={(status.danger?.total ?? 0) > 0}
            key={name}
            label={labels[title]?.() ?? title}
            order={UniqueId(name)}
            value={aggregated.total ? (aggregated.done / aggregated.total) * 100 : 0}
          />
        ))
      ) : (
        <Column detail="100%/100%" label={done} value={100} />
      )}
    </div>
  )
}

// Mirrors apps/frontend's bootstrap pair (state/tracking.ts holds the state,
// Messages.tsx renders it as a strip of per-task bars) into one sonner toast.
// Mounted/dismissed on `is_loading` alone — a boolean computed that flips once
// when the first task starts and once when the store empties, NOT on every
// status message (a preact/signals computed only notifies when its value
// changes). So `toast.custom` runs once per run — sonner's enter/exit animation
// never re-triggers — while the live per-task updates happen inside
// TrackingBars, which preact diffs in place. The store keeps finished trackers
// around for CLEANUP_DELAY before dropping them, so the completed bars linger
// before `is_loading` flips false and the toast dismisses. `toast.custom` +
// `unstyled` so sonner adds none of its own chrome (no spinner, no icon-column
// `data-content`/`data-title` wrappers that steal width) — the card below is
// ours, spanning edge to edge.
export function TrackingToaster({
  tracking,
  position = "bottom-left",
}: {
  tracking: ReturnType<typeof CreateTracking>
  position?: NonNullable<Parameters<typeof Toaster>[0]>["position"]
}) {
  const labels = useTranslation("trackers")
  const descriptions = useTranslation("trackers_desc")
  const pdfview = useTranslationTyped("pdfview")
  const loading = tracking.is_loading
  const done = pdfview.LOADING_DONE()

  useEffect(() => {
    if (!loading) {
      toast.dismiss(TOAST_ID)
      return
    }
    toast.custom(
      () => (
        <div className="w-full rounded-lg border bg-popover px-4 py-3 text-popover-foreground shadow-lg">
          <TrackingBars
            descriptions={descriptions}
            done={done}
            labels={labels}
            tracking={tracking}
          />
        </div>
      ),
      { duration: POS_INFINITY, id: TOAST_ID, style: { width: "28rem" }, unstyled: true }
    )
  }, [loading, labels, descriptions, done, tracking])

  // richColors so plain toast.success / toast.error (e.g. the debug "Cache
  // cleared") get their green/red treatment; the tracking toast opts out via
  // `unstyled` above so it's unaffected.
  return <Toaster position={position} richColors />
}
